import type { MfRemoteDebugConfig, MfRemoteDebugEvent } from './mf-debug.types';

/**
 * Module Federation Remote Debug Service Lightweight debugging for remote applications
 *
 * Enable via: localStorage.setItem('symphony.mf.debug', 'true')
 */

// Cast window to any for dynamic property access
// Type safety is ensured via separate .d.ts file for consumers
const win = window as any;

/**
 * Remote Debug Service for Module Federation remote applications Provides consistent logging across all remote apps
 * (admin, assessment, audit, template)
 */
export class MfRemoteDebugService {
  private config: MfRemoteDebugConfig;
  private events: MfRemoteDebugEvent[] = [];
  private startTime: number;

  constructor(remoteName: string) {
    this.config = {
      enabled: localStorage.getItem('symphony.mf.debug') === 'true',
      remoteName,
      captureErrors: localStorage.getItem('symphony.mf.debug') === 'true',
    };
    this.startTime = performance.now();

    this.initializeErrorHandlers();
    this.exposeDebugInterface();

    if (this.config.enabled) {
      this.log('info', `Remote Debug Mode: ${remoteName}`, {
        timestamp: new Date().toISOString(),
      });
    }
  }

  /** Check if debug mode is enabled */
  isEnabled(): boolean {
    return this.config.enabled;
  }

  /** Get remote name */
  getRemoteName(): string {
    return this.config.remoteName;
  }

  /** Log bootstrap start */
  logBootstrapStart(): void {
    this.recordEvent({
      type: 'remote-bootstrap-start',
      remoteName: this.config.remoteName,
      timestamp: Date.now(),
    });

    this.log('info', 'Remote bootstrap starting...');
  }

  /** Log bootstrap success */
  logBootstrapSuccess(): void {
    const duration = performance.now() - this.startTime;
    this.recordEvent({
      type: 'remote-bootstrap-success',
      remoteName: this.config.remoteName,
      timestamp: Date.now(),
      duration,
    });

    this.log('info', `Remote bootstrap completed`, { duration: `${duration.toFixed(2)}ms` });

    // Mark remote as ready
    this.markRemoteReady();
  }

  /** Log bootstrap error */
  logBootstrapError(error: unknown): void {
    this.recordEvent({
      type: 'remote-bootstrap-error',
      remoteName: this.config.remoteName,
      timestamp: Date.now(),
      error,
    });

    console.error(`Remote bootstrap failed:`, error);

    if (this.config.enabled) {
      win.__MF_REMOTE_BOOTSTRAP_ERROR__ = {
        remoteName: this.config.remoteName,
        error,
        timestamp: Date.now(),
      };
    }
  }

  /** Mark remote as ready */
  private markRemoteReady(): void {
    this.recordEvent({
      type: 'remote-ready',
      remoteName: this.config.remoteName,
      timestamp: Date.now(),
    });

    if (this.config.enabled) {
      if (!win.__MF_REMOTES_READY__) {
        win.__MF_REMOTES_READY__ = {};
      }
      win.__MF_REMOTES_READY__[this.config.remoteName] = {
        timestamp: Date.now(),
        duration: performance.now() - this.startTime,
      };
    }
  }

  /** Log route exposure */
  logRouteExposed(routePath: string): void {
    this.recordEvent({
      type: 'route-exposed',
      remoteName: this.config.remoteName,
      timestamp: Date.now(),
      data: { routePath },
    });

    this.log('verbose', `Route exposed: ${routePath}`);
  }

  /** Log component registration */
  logComponentRegistered(componentName: string): void {
    this.recordEvent({
      type: 'component-registered',
      remoteName: this.config.remoteName,
      timestamp: Date.now(),
      data: { componentName },
    });

    this.log('verbose', `Component registered: ${componentName}`);
  }

  /** Log unhandled error */
  logUnhandledError(error: unknown, context?: string): void {
    this.recordEvent({
      type: 'unhandled-error',
      remoteName: this.config.remoteName,
      timestamp: Date.now(),
      error,
      data: { context },
    });

    console.error(`Unhandled error in ${this.config.remoteName}:`, error, context ? `Context: ${context}` : '');

    if (this.config.enabled) {
      if (!win.__MF_REMOTE_UNHANDLED_ERRORS__) {
        win.__MF_REMOTE_UNHANDLED_ERRORS__ = [];
      }
      win.__MF_REMOTE_UNHANDLED_ERRORS__.push({
        remoteName: this.config.remoteName,
        error,
        context,
        timestamp: Date.now(),
      });
    }
  }

  /** Generic logging method */
  private log(level: 'info' | 'warn' | 'error' | 'verbose', message: string, data?: Record<string, unknown>): void {
    if (!this.config.enabled) return;

    const prefix = `[MF:${this.config.remoteName}]`;
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];

    if (data) {
      console[level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'info'](
        `${prefix} ${timestamp} ${message}`,
        data
      );
    } else {
      console[level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'info'](`${prefix} ${timestamp} ${message}`);
    }
  }

  /** Record event in history */
  private recordEvent(event: MfRemoteDebugEvent): void {
    if (!this.config.enabled) return;

    this.events.push(event);
    // Keep only last 50 events per remote
    if (this.events.length > 50) {
      this.events.shift();
    }
  }

  /** Initialize error handlers */
  private initializeErrorHandlers(): void {
    if (!this.config.captureErrors) return;

    window.addEventListener('error', (event) => {
      // Only log if error originated from this remote's context
      this.logUnhandledError(event.error, 'window.onerror');
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.logUnhandledError(event.reason, 'unhandledrejection');
    });
  }

  /** Expose debug interface on window */
  private exposeDebugInterface(): void {
    if (!this.config.enabled) return;

    if (!win.__MF_REMOTE_DEBUG__) {
      win.__MF_REMOTE_DEBUG__ = {};
    }

    win.__MF_REMOTE_DEBUG__[this.config.remoteName] = {
      service: this,
      getEvents: () => this.events,
      getConfig: () => this.config,
      clearEvents: () => (this.events = []),
      printSummary: () => this.printDebugSummary(),
    };
  }

  /** Print debug summary */
  private printDebugSummary(): void {
    console.group(`Remote Debug Summary: ${this.config.remoteName}`);
    console.info('Total Events:', this.events.length);
    console.info('All Events:', this.events);
    console.info('Ready Status:', win.__MF_REMOTES_READY__?.[this.config.remoteName]);
    console.info('Bootstrap Error:', win.__MF_REMOTE_BOOTSTRAP_ERROR__);
    console.groupEnd();
  }

  /** Get all recorded events */
  getEvents(): MfRemoteDebugEvent[] {
    return [...this.events];
  }

  /** Clear all recorded events */
  clearEvents(): void {
    this.events = [];
  }
}
